// thumbnailCache.ts
// Кэш миниатюр страниц PDF в IndexedDB. Без React, без внешних импортов —
// переиспользуется и в pda-cfront. Ни одна экспортируемая функция не бросает:
// любая ошибка кэша деградирует в «промах», и миниатюра просто рендерится заново.
//
// Почему отдельная БД, а не новый store в pda-attachment-cache: бамп DB_VERSION
// там триггерит onblocked в уже открытых вкладках, а его обработчик навсегда
// отключает весь кэш вложений (attachmentCache.ts, request.onblocked -> disable()).
// Ради миниатюр ломать кэш файлов нельзя.

export const THUMB_TOTAL_CAP_BYTES = 60 * 1024 * 1024;
export const THUMB_DOC_CAP_BYTES = 20 * 1024 * 1024;

const DB_NAME = 'pda-pdf-thumb-cache';
const DB_VERSION = 1;
const THUMBS_STORE = 'thumbs';
const DOCS_STORE = 'docs';
const THUMBS_INDEX_DOC = 'by_docId';
const OPEN_TIMEOUT_MS = 3000;

// Версия схемы ключа. Меняется при смене формата/размера миниатюр, чтобы старые
// записи просто перестали находиться (и вытеснились по LRU), без миграций.
const SCHEMA = 'v1';

export interface ThumbRecord {
    key: string;
    docId: string;
    buffer: ArrayBuffer;
    type: string;
    w: number;
    h: number;
}

interface DocRecord {
    docId: string;
    size: number;
    lastUsedAt: number;
}

type CacheState = 'unknown' | 'ok' | 'disabled';

let state: CacheState = 'unknown';
let dbPromise: Promise<IDBDatabase | null> | null = null;

export function thumbKey(docId: string, width: number, page: number): string {
    return `${SCHEMA}:${docId}:${width}:${page}`;
}

function disable(): null {
    state = 'disabled';
    return null;
}

// Тот же приём, что и в attachmentCache: кэшируем промис реального open(),
// а таймаут навешиваем отдельно на каждый вызов (см. openDb) — иначе таймаут
// резолвит закэшированный промис в null и «отравляет» его навсегда.
function createRealOpenPromise(): Promise<IDBDatabase | null> {
    return new Promise<IDBDatabase | null>((resolve) => {
        if (typeof indexedDB === 'undefined') {
            resolve(disable());
            return;
        }

        let request: IDBOpenDBRequest;
        try {
            request = indexedDB.open(DB_NAME, DB_VERSION);
        } catch {
            resolve(disable());
            return;
        }

        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(THUMBS_STORE)) {
                const thumbs = db.createObjectStore(THUMBS_STORE, { keyPath: 'key' });
                thumbs.createIndex(THUMBS_INDEX_DOC, 'docId');
            }
            if (!db.objectStoreNames.contains(DOCS_STORE)) {
                db.createObjectStore(DOCS_STORE, { keyPath: 'docId' });
            }
        };

        request.onsuccess = () => {
            const db = request.result;
            db.onversionchange = () => {
                db.close();
                state = 'disabled';
                dbPromise = null;
            };
            state = 'ok';
            resolve(db);
        };

        request.onerror = () => resolve(disable());
        request.onblocked = () => resolve(disable());
    });
}

function openDb(): Promise<IDBDatabase | null> {
    if (state === 'disabled') return Promise.resolve(null);
    if (!dbPromise) dbPromise = createRealOpenPromise();

    return Promise.race([
        dbPromise,
        new Promise<IDBDatabase | null>((resolve) => setTimeout(() => resolve(null), OPEN_TIMEOUT_MS)),
    ]);
}

function reqToPromise<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function txDone(tx: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    });
}

/** Пакетное чтение — одна транзакция на весь batch ставших видимыми страниц. */
export async function getThumbs(keys: string[]): Promise<Map<string, ThumbRecord>> {
    const result = new Map<string, ThumbRecord>();
    if (!keys.length) return result;

    try {
        const db = await openDb();
        if (!db) return result;

        const tx = db.transaction(THUMBS_STORE, 'readonly');
        const store = tx.objectStore(THUMBS_STORE);
        const records = await Promise.all(keys.map((k) => reqToPromise(store.get(k))));
        for (const record of records as Array<ThumbRecord | undefined>) {
            if (record) result.set(record.key, record);
        }
        return result;
    } catch {
        return result;
    }
}

async function readAllDocs(db: IDBDatabase): Promise<DocRecord[]> {
    // getAll() бежит только по docs (десятки записей), не по thumbs, где на
    // 300-страничном документе тысячи строк — их подъём в память ради учёта
    // размера съел бы больше, чем весь кэш экономит.
    const tx = db.transaction(DOCS_STORE, 'readonly');
    return (await reqToPromise(tx.objectStore(DOCS_STORE).getAll())) as DocRecord[];
}

async function deleteDoc(db: IDBDatabase, docId: string): Promise<void> {
    const tx = db.transaction([THUMBS_STORE, DOCS_STORE], 'readwrite');
    const index = tx.objectStore(THUMBS_STORE).index(THUMBS_INDEX_DOC);
    const cursorReq = index.openKeyCursor(IDBKeyRange.only(docId));
    const thumbs = tx.objectStore(THUMBS_STORE);

    await new Promise<void>((resolve, reject) => {
        cursorReq.onsuccess = () => {
            const cursor = cursorReq.result;
            if (!cursor) {
                resolve();
                return;
            }
            thumbs.delete(cursor.primaryKey);
            cursor.continue();
        };
        cursorReq.onerror = () => reject(cursorReq.error);
    });

    tx.objectStore(DOCS_STORE).delete(docId);
    await txDone(tx);
}

/** Вытеснение целыми документами: держать полдокумента в кэше смысла нет. */
async function evictToFit(db: IDBDatabase, incomingDocId: string, incomingSize: number): Promise<void> {
    const docs = await readAllDocs(db);
    let total = docs.reduce((sum, d) => sum + d.size, 0) + incomingSize;
    if (total <= THUMB_TOTAL_CAP_BYTES) return;

    const victims = docs
        .filter((d) => d.docId !== incomingDocId)
        .sort((a, b) => a.lastUsedAt - b.lastUsedAt);

    for (const victim of victims) {
        if (total <= THUMB_TOTAL_CAP_BYTES) break;
        await deleteDoc(db, victim.docId);
        total -= victim.size;
    }
}

/**
 * Пакетная запись. Вызывающий копит миниатюры и сбрасывает пачкой, чтобы не
 * открывать транзакцию на каждую страницу.
 */
export async function putThumbs(docId: string, records: ThumbRecord[]): Promise<void> {
    if (!records.length) return;

    try {
        const db = await openDb();
        if (!db) return;

        const incoming = records.reduce((sum, r) => sum + r.buffer.byteLength, 0);

        const metaTx = db.transaction(DOCS_STORE, 'readonly');
        const existing = (await reqToPromise(metaTx.objectStore(DOCS_STORE).get(docId))) as DocRecord | undefined;
        const currentDocSize = existing?.size ?? 0;

        // Документ уже занял свою квоту — дальше просто не кэшируем. Уже
        // записанные страницы при этом остаются валидными.
        if (currentDocSize + incoming > THUMB_DOC_CAP_BYTES) return;

        await evictToFit(db, docId, incoming);

        const tx = db.transaction([THUMBS_STORE, DOCS_STORE], 'readwrite');
        const thumbs = tx.objectStore(THUMBS_STORE);
        for (const record of records) thumbs.put(record);
        tx.objectStore(DOCS_STORE).put({
            docId,
            size: currentDocSize + incoming,
            lastUsedAt: Date.now(),
        } satisfies DocRecord);

        try {
            await txDone(tx);
        } catch (err) {
            if (err instanceof DOMException && err.name === 'QuotaExceededError') state = 'disabled';
        }
    } catch {
        // промах записи не должен ломать показ миниатюр
    }
}

/** Отметить документ как недавно использованный — для LRU-вытеснения. */
export async function touchDoc(docId: string): Promise<void> {
    try {
        const db = await openDb();
        if (!db) return;

        const tx = db.transaction(DOCS_STORE, 'readwrite');
        const store = tx.objectStore(DOCS_STORE);
        const doc = (await reqToPromise(store.get(docId))) as DocRecord | undefined;
        if (doc) store.put({ ...doc, lastUsedAt: Date.now() } satisfies DocRecord);
        await txDone(tx);
    } catch {
        // деградация качества LRU, не критично
    }
}

export async function clearThumbnailCache(): Promise<void> {
    try {
        const db = await openDb();
        if (!db) return;

        const tx = db.transaction([THUMBS_STORE, DOCS_STORE], 'readwrite');
        tx.objectStore(THUMBS_STORE).clear();
        tx.objectStore(DOCS_STORE).clear();
        await txDone(tx);
    } catch {
        // нечего чистить или кэш недоступен
    }
}
